import React from 'react';
import { FilterX } from 'lucide-react';
import clsx from 'clsx';
import type {
  TableColumn,
  ColumnFilters,
  ColumnFilterValue,
  AnyOfFilter,
  BlankFilter,
  DateRangeFilter as DateRangeFilterValue,
  FilterOption,
  NumberRangeFilter as NumberRangeFilterValue,
  TextFilter as TextFilterValue,
} from '@app-types';
import { UNASSIGNED_FILTER_VALUE } from '@utils/columnFilterUrl';
import DateRangeFilter from './DateRangeFilter';
import NumberRangeFilter from './NumberRangeFilter';
import SelectFilter from './SelectFilter';
import TextFilter from './TextFilter';
import styles from './ColumnFiltersRow.module.css';

interface ColumnFiltersRowProps {
  columns: TableColumn[];
  filterValues: ColumnFilters;
  onFilterChange: (columnKey: string, value: ColumnFilterValue | null) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
}

/** A text filter's value, with the bare-string shorthand expanded. */
function textValueOf(value: ColumnFilterValue | undefined): TextFilterValue | BlankFilter | undefined {
  if (value === undefined) return undefined;
  if (typeof value === 'string') return { kind: 'text', value };
  return value.kind === 'text' || value.kind === 'blank' ? value : undefined;
}

function dateRangeValueOf(
  value: ColumnFilterValue | undefined
): DateRangeFilterValue | BlankFilter | undefined {
  return typeof value === 'object' && (value.kind === 'dateRange' || value.kind === 'blank')
    ? value
    : undefined;
}

function numberRangeValueOf(
  value: ColumnFilterValue | undefined
): NumberRangeFilterValue | BlankFilter | undefined {
  return typeof value === 'object' && (value.kind === 'numberRange' || value.kind === 'blank')
    ? value
    : undefined;
}

/**
 * A select column says "no value" as one more option, so it can be picked
 * together with real values ("Unassigned or Finance"). It goes first: it is the
 * one option every such column shares.
 */
function selectOptionsOf(column: TableColumn): FilterOption[] {
  const options = column.filterOptions ?? [];
  return column.emptyFilterLabel
    ? [{ value: UNASSIGNED_FILTER_VALUE, label: column.emptyFilterLabel }, ...options]
    : options;
}


function anyOfValueOf(value: ColumnFilterValue | undefined): AnyOfFilter | undefined {
  return typeof value === 'object' && value?.kind === 'anyOf' ? value : undefined;
}

/**
 * Row of filter controls below the table header.
 *
 * Switches on `filterType` first, so a column can be both filterable and
 * custom-rendered. The previous order (`filterable ? text : filterNode ? ...`)
 * made those mutually exclusive, which is why typed filters could not coexist
 * with the filter row. `filterNode` is retained for the Reassessment column.
 */
const ColumnFiltersRow: React.FC<ColumnFiltersRowProps> = ({
  columns,
  filterValues,
  onFilterChange,
  onClearAll,
  hasActiveFilters
}) => {
  const renderFilter = (column: TableColumn) => {
    const label = typeof column.label === 'string' ? column.label : column.key;
    const value = filterValues[column.key];

    if (column.filterable === false) {
      return column.filterNode ?? <div className={styles.columnFilterPlaceholder} />;
    }

    if (column.filterType === 'dateRange') {
      return (
        <DateRangeFilter
          columnLabel={label}
          value={dateRangeValueOf(value)}
          onChange={(next) => onFilterChange(column.key, next)}
          emptyFilterLabel={column.emptyFilterLabel}
        />
      );
    }

    if (column.filterType === 'numberRange') {
      return (
        <NumberRangeFilter
          columnLabel={label}
          value={numberRangeValueOf(value)}
          onChange={(next) => onFilterChange(column.key, next)}
          emptyFilterLabel={column.emptyFilterLabel}
        />
      );
    }

    if (column.filterType === 'select') {
      return (
        <SelectFilter
          columnLabel={label}
          options={selectOptionsOf(column)}
          value={anyOfValueOf(value)}
          onChange={(next) => onFilterChange(column.key, next)}
        />
      );
    }

    return (
      <TextFilter
        columnLabel={label}
        value={textValueOf(value)}
        onChange={(next) => onFilterChange(column.key, next)}
        emptyFilterLabel={column.emptyFilterLabel}
        maxLength={column.maxFilterLength}
      />
    );
  };

  return (
    <tr className={styles.columnFiltersRow}>
      {columns.map((column) => (
        <th key={column.key} className={styles.columnFilterCell}>
          {renderFilter(column)}
        </th>
      ))}
      {/* Sticky Clear All button - always visible even with horizontal scroll */}
      {hasActiveFilters && (
        <th className={clsx(styles.columnFilterCell, styles.columnFilterCellSticky)}>
          <button
            type="button"
            className={styles.clearAllFiltersBtn}
            onClick={onClearAll}
            title="Clear all filters"
          >
            <FilterX size={14} />
            <span>Clear</span>
          </button>
        </th>
      )}
    </tr>
  );
};

export default ColumnFiltersRow;
